import {
    ACTIVE_SCREEN_KEY,
    setScreenData,
    formatMessage,
    setErrorScreenMessage,
    getScreenData,
    Store,
    removeScreenData,
} from 'cw-ui-core';
import { put, call, all } from 'redux-saga/effects';
import Locale from 'src/i18n';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import map from 'lodash/map';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import sortBy from 'lodash/sortBy';
import isArray from 'lodash/isArray';
import { EDIT_USER_DRAWER, EDIT_GUEST_USER_DRAWER } from 'src/features/users/constants/drawerConstants';
import { ADD_ROLE_DRAWER, EDIT_ROLE_DRAWER } from 'src/features/identity-roles/constants';
import { toArray } from 'src/util/Util';
import { GET, getHeaders, autoHideMessages } from './ApiService';
import { sortApplicationRoles } from 'src/features/application-roles/utils';

export function* fetchConnectedApps(screenKey = ACTIVE_SCREEN_KEY, queryParams = '') {
    const url = !isEmpty(queryParams) ? `/iambff/v1/connected-apps?${queryParams}` : '/iambff/v1/connected-apps';
    const result = yield call(GET, url, getHeaders());

    const response = {};
    if (result.isSuccess) {
        response.connectedApps = result.response;
        yield put(setScreenData(['products'], result.response, screenKey));

        const singleRoleApps = filter(result?.response, app => {
            if (app?.authorization?.roleAssignment?.hasSingleAppRoleConstraint === true) return app.clientId;
            return null;
        }).map(x => x?.clientId);
        yield put(setScreenData(['singleRoleApps'], singleRoleApps, screenKey));
    } else {
        response.error = [
            {
                screenMessage: formatMessage(Locale.failedLoadConnectedApps),
                errorMessages: [],
            },
        ];
    }

    return response;
}

export function* getConnectedAppInstanceName(screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isInstanceNameReady'], false, screenKey));
    const queryParamsString = 'fields=instanceFriendlyName,applicationBaseName,iconName';
    const { connectedApps = [] } = yield call(fetchConnectedApps, screenKey, queryParamsString);
    yield put(setScreenData(['connectedAppInstanceNames'], connectedApps, screenKey));
    yield put(setScreenData(['isInstanceNameReady'], true, screenKey));
}

export function* loadAuthOnlyConnectedAppsData(connectedApps = [], screenKey = ACTIVE_SCREEN_KEY) {
    const authOnlyApps = filter(connectedApps, connectedApp => {
        return !connectedApp.authorizationEnabled && connectedApp.userAccessControlEnabled;
    });
    const authOnlyAppIds = map(authOnlyApps, app => app.connectedAppId);
    const userId = getScreenData(Store().getState().screen, ['activeUserId']);
    const result = yield call(GET, `/clients/users/${userId}/clients`, getHeaders('1.0'));

    if (result.isSuccess && result.response.length > 0 && authOnlyAppIds.length > 0) {
        const clientsAssociatedWithUser = result.response;
        const clientsAssociatedWithUserMap = {};
        forEach(clientsAssociatedWithUser, client => {
            clientsAssociatedWithUserMap[client] = true;
        });

        const clientsToggledOnArray = filter(authOnlyAppIds, authOnlyAppId => {
            return clientsAssociatedWithUserMap[authOnlyAppId];
        });
        const clientsToggledOnMap = {};
        forEach(clientsToggledOnArray, client => {
            clientsToggledOnMap[client] = true;
        });
        yield put(setScreenData(['AuthenticationOnlyConnectedApp'], clientsToggledOnMap, screenKey));
    }
}

export function* retryFetchingMetadata(connectedAppId, screenKey) {
    const connectedAppsRedux = getScreenData(Store().getState().screen, 'connectedApps', screenKey);
    let connectedApps = toArray(connectedAppsRedux);

    // take app in loading state
    yield put(setScreenData(['LoadingApps', connectedAppId], true, screenKey));

    // hit /connectedApps api with provided connectedAppId
    const result = yield call(GET, `/iambff/v1/connected-apps/${connectedAppId}`, getHeaders());

    // check response and see if there are any applicationRoles (to set special flag on/off)
    if (result.isSuccess) {
        const applicationRoles = get(result.response, 'authorization.roleAssignment.applicationRoles', []);

        connectedApps = map(connectedApps, app => {
            if (isEqual(app.connectedAppId, connectedAppId)) {
                const updatedApp = { ...app, ...result.response };
                updatedApp.isAppRolesPresent = size(applicationRoles) > 0;
                return updatedApp;
            }
            return app;
        });
        yield put(setScreenData('connectedApps', connectedApps, screenKey));
    }

    // take app to normal state ( not loading )
    yield put(setScreenData(['LoadingApps', connectedAppId], false, screenKey));
}

export function* loadConnectedAppsDetails(
    screenKey = ACTIVE_SCREEN_KEY,
    identityRoleId,
    userName = '',
    isNewIdentityRole = false
) {
    yield put(setScreenData(['isConnectedAppsReady'], false, screenKey));
    // connected Apps list for identity roles
    yield put(setScreenData(['isConnectedAppsLoading'], true, screenKey));

    const queryParameterString =
        isEqual(screenKey, EDIT_USER_DRAWER) || isEqual(screenKey, EDIT_GUEST_USER_DRAWER)
            ? `userName=${userName}`
            : '';
    const result = yield call(fetchConnectedApps, screenKey, queryParameterString);

    if (!isEmpty(result.errors)) {
        for (let i = 0; i < result.errors.length; i += 1) {
            yield put(
                setErrorScreenMessage(result.errors[i].screenMessage, false, result.errors[i].errorMessages, screenKey)
            );
        }
        return;
    }

    const connectedApps = map(get(result, 'connectedApps', []), client => {
        // checking if there are certain apps with authorization enabled with no roles under them
        const applicationRoles = get(client, 'authorization.roleAssignment.applicationRoles', []);

        const data = {
            ...client,
            connectedAppId: client.clientId,
            isAppRolesPresent: !(client.authorizationEnabled && isEmpty(applicationRoles)),
        };
        return data;
    });

    let connectedAppsPrepopulatedData;
    const fetchAppMappingsUrls = [];

    forEach(connectedApps, app => {
        const isAuthorizationEnabled = get(app, ['authorizationEnabled'], false);
        if (!isEmpty(identityRoleId)) {
            if (screenKey === ADD_ROLE_DRAWER || screenKey === EDIT_ROLE_DRAWER || isNewIdentityRole) {
                if (isAuthorizationEnabled) {
                    fetchAppMappingsUrls.push(
                        call(
                            GET,
                            `/iambff/v1/identity-roles/${identityRoleId}/connected-app-mappings/${app.clientId}`,
                            getHeaders()
                        )
                    );
                }
            } else {
                fetchAppMappingsUrls.push(
                    call(
                        GET,
                        `/iambff/v1/identity-roles/${identityRoleId}/connected-app-mappings/${app.clientId}`,
                        getHeaders()
                    )
                );
            }
        }
    });

    if (!isEmpty(fetchAppMappingsUrls)) {
        const connectedAppMappingResponses = yield all(fetchAppMappingsUrls);
        connectedAppsPrepopulatedData = map(connectedAppMappingResponses, ({ response }) => response);
    }

    if (!isEmpty(connectedApps)) {
        const sortedConnectedApps = sortBy(connectedApps, [
            app => {
                return !get(app, 'authorizationEnabled', false);
            },
            app => {
                return (
                    get(app, 'authorization.roleAssignment.hasSingleAppRoleConstraint', false) ||
                    get(app, 'authorization.roleAssignment.forbidIdentityRoleAssignment', false)
                );
            },
            app => {
                return !get(app, 'userAccessControlEnabled', false);
            },
        ]);
        yield put(setScreenData(['connectedApps'], sortedConnectedApps, screenKey));
        if (isEqual(screenKey, EDIT_USER_DRAWER) || isEqual(screenKey, EDIT_GUEST_USER_DRAWER)) {
            yield call(loadAuthOnlyConnectedAppsData, connectedApps, screenKey);
        }
        if (connectedAppsPrepopulatedData) {
            yield put(setScreenData(['connectedAppsPrepopulatedData'], connectedAppsPrepopulatedData, screenKey));
        }
        yield put(setScreenData(['isConnectedAppsReady'], true, screenKey));
        // connected Apps list for identity roles
        yield put(setScreenData(['isConnectedAppsLoading'], false, screenKey));
    } else {
        const error = formatMessage(Locale.failedLoadConnectedApps);
        yield put(setErrorScreenMessage(error, false, 'Failed to load connected apps.', screenKey));
        yield put(setScreenData(['isConnectedAppsReady'], true, screenKey));
        // connected Apps list for identity roles
        yield put(setScreenData(['isConnectedAppsLoading'], false, screenKey));
    }
}

export function* loadDefaultAppRoleMappings(
    userId,
    identityRoles = [],
    isConnectedAppsPrepopulatedDataReady = false,
    screenKey = ACTIVE_SCREEN_KEY
) {
    if (userId && isArray(identityRoles) && !isConnectedAppsPrepopulatedDataReady) {
        const roles = identityRoles.join();
        const result = yield call(
            GET,
            `/iambff/v1/users/${userId}/app-role-mappings?identity_role_ids=${roles}`,
            getHeaders()
        );
        if (result.isSuccess) {
            const assignedAppRolesInfo = {};
            forEach(get(result.response, 'connectedApps'), app => {
                const appId = get(app, 'connectedAppId');
                const roleDetails = {};
                forEach(get(app, 'applicationRoles'), role => {
                    const roleId = get(role, 'roleId');
                    roleDetails[roleId] = { ...role };
                });
                assignedAppRolesInfo[appId] = { ...roleDetails };
            });
            yield put(setScreenData(['assignedAppRolesInfo'], assignedAppRolesInfo, screenKey));
            yield put(setScreenData(['connectedAppsPrepopulatedData'], result.response, screenKey));
        } else {
            const error = formatMessage(Locale.failedLoadConnectedApps);
            yield put(setErrorScreenMessage(error, false, formatMessage(Locale.failedLoadResources), screenKey));
        }
    }
}

export function* loadConnectedAppsAddUserDetails(identityRoleId, identityRoleName, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isConnectedAppsReady'], false, screenKey));
    const result = yield call(fetchConnectedApps, screenKey);

    if (!isEmpty(result.errors)) {
        for (let i = 0; i < result.errors.length; i += 1) {
            yield put(
                setErrorScreenMessage(result.errors[i].screenMessage, false, result.errors[i].errorMessages, screenKey)
            );
        }
        return;
    }

    const connectedApps = map(get(result, 'connectedApps'), client => {
        // checking if there are certain apps with authorization enabled with no roles under them
        const applicationRoles = get(client, 'authorization.roleAssignment.applicationRoles', []);

        const data = {
            ...client,
            connectedAppId: client.clientId,
            isAppRolesPresent: !(client.authorizationEnabled && isEmpty(applicationRoles)),
        };
        return data;
    });

    let connectedAppsPrepopulatedData;

    const fetchAppMappingsUrls = [];

    forEach(connectedApps, app => {
        if (identityRoleId && app.authorizationEnabled) {
            fetchAppMappingsUrls.push(
                call(
                    GET,
                    `/iambff/v1/identity-roles/${identityRoleId}/connected-app-mappings/${app.clientId}`,
                    getHeaders()
                )
            );
        }
    });

    if (!isEmpty(fetchAppMappingsUrls)) {
        const connectedAppMappingResponses = yield all(fetchAppMappingsUrls);
        connectedAppsPrepopulatedData = map(connectedAppMappingResponses, ({ response }) => {
            const applicationRoles = get(response, 'applicationRoles');
            if (!isEmpty(applicationRoles)) {
                const appRoles = map(applicationRoles, appRole => {
                    return {
                        ...appRole,
                        identityRoleNames: [identityRoleName],
                    };
                });
                return {
                    ...response,
                    applicationRoles: appRoles,
                };
            }
            return {
                ...response,
            };
        });
    }

    if (!isEmpty(connectedApps)) {
        const sortedConnectedApps = sortBy(connectedApps, [
            app => {
                return !get(app, 'authorizationEnabled', false);
            },
            app => {
                return (
                    get(app, 'authorization.roleAssignment.hasSingleAppRoleConstraint', false) ||
                    get(app, 'authorization.roleAssignment.forbidIdentityRoleAssignment', false)
                );
            },
            app => {
                return !get(app, 'userAccessControlEnabled', false);
            },
        ]);
        yield put(setScreenData(['connectedApps'], sortedConnectedApps, screenKey));
        if (connectedAppsPrepopulatedData) {
            yield put(
                setScreenData(
                    ['connectedAppsReadOnlyData', `${identityRoleId}`],
                    connectedAppsPrepopulatedData,
                    screenKey
                )
            );
        }
        yield put(setScreenData(['isConnectedAppsReady'], true, screenKey));
    } else {
        const error = formatMessage(Locale.failedLoadConnectedApps);
        yield put(setErrorScreenMessage(error, false, 'Failed to load connected apps.', screenKey));
        yield put(setScreenData(['isConnectedAppsReady'], true, screenKey));
    }
}

export function* loadConnectedAppsResources(connectedAppId, resourceListId, screenKey = ACTIVE_SCREEN_KEY) {
    const connectedAppsFromState = getScreenData(Store().getState().screen, ['connectedApps'], screenKey);
    const connectedAppsData = connectedAppsFromState ? connectedAppsFromState.toJS() : [];
    const connectedAppIndex = connectedAppsData.findIndex(item => get(item, 'connectedAppId') === connectedAppId);
    const connectedAppsFromStateIsLoading = getScreenData(
        Store().getState().screen,
        ['connectedApps', `${connectedAppIndex}`, 'authorization', 'isResourceLoading'],
        screenKey
    );

    if (connectedAppId && resourceListId && !connectedAppsFromStateIsLoading) {
        yield put(
            setScreenData(
                ['connectedApps', `${connectedAppIndex}`, 'authorization', 'isResourceLoading'],
                true,
                screenKey
            )
        );
        const fetchedResources = yield call(
            GET,
            `/iambff/v1/connected-apps/${connectedAppId}/resource-lists/${resourceListId}`,
            getHeaders()
        );

        if (!isEmpty(fetchedResources.response)) {
            yield put(
                setScreenData(
                    [
                        'connectedApps',
                        `${connectedAppIndex}`,
                        'authorization',
                        'resourcesList',
                        `${resourceListId}`,
                        'resources',
                    ],
                    fetchedResources.response,
                    screenKey
                )
            );
            yield put(
                removeScreenData(
                    ['connectedApps', `${connectedAppIndex}`, 'authorization', 'isResourceLoading'],
                    screenKey
                )
            );
        } else {
            yield put(
                removeScreenData(
                    ['connectedApps', `${connectedAppIndex}`, 'authorization', 'isResourceLoading'],
                    screenKey
                )
            );
            yield put(setErrorScreenMessage(formatMessage(Locale.failedLoadResources), false, [], screenKey));
            yield call(autoHideMessages, screenKey);
        }
    }
}

export function* fetchConnectedAppMappings(identityRoleId, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isRoleMappingReady'], false, screenKey));

    const connectedAppsState = getScreenData(Store().getState().screen, ['products'], screenKey);
    let connectedApps = connectedAppsState ? connectedAppsState.toJS() : [];

    if (isEmpty(connectedApps)) {
        const result = yield call(fetchConnectedApps, screenKey);
        connectedApps = result.connectedApps;
    }

    if (!isEmpty(connectedApps)) {
        const fetchAppMappingsUrls = [];

        for (let i = 0; i < connectedApps.length; i += 1) {
            if (connectedApps[i].authorizationEnabled) {
                fetchAppMappingsUrls.push(
                    call(
                        GET,
                        `/iambff/v1/identity-roles/${identityRoleId}/connected-app-mappings/${connectedApps[i].clientId}`,
                        getHeaders()
                    )
                );
            }
        }

        if (!isEmpty(fetchAppMappingsUrls)) {
            const connectedAppMappingResponses = yield all(fetchAppMappingsUrls);
            const connectedAppsPrepopulatedData = map(connectedAppMappingResponses, ({ response }) => response);
            yield put(setScreenData(['identityRoles', `${identityRoleId}`], connectedAppsPrepopulatedData, screenKey));
            yield put(setScreenData(['isRoleMappingReady'], true, screenKey));
        }
    }
}

export function* loadConnectedApp(clientId, screenKey = ACTIVE_SCREEN_KEY) {
    yield put(setScreenData(['isConnectedAppsReady'], false));
    const result = yield call(GET, `/iambff/v1/connected-apps/${clientId}`, getHeaders());
    if (result.isSuccess) {
        const connectedApp = result.response;

        yield put(setScreenData(['connectedApps'], [connectedApp], screenKey));
        yield put(setScreenData(['isConnectedAppsReady'], true));
    } else {
        if (!isEmpty(result.errors)) {
            setErrorScreenMessage(result.errors[0].screenMessage, false, result.errors[0].errorMessages, screenKey);
        }
        yield put(setScreenData(['isConnectedAppsReady'], true));
    }
}

export function* loadConnectedAppRoles(clientId, screenKey = ACTIVE_SCREEN_KEY) {
    const isRolesReadyPath = ['connectedApp', `${clientId}`, 'isRolesReady'];
    const rolesPath = ['connectedApp', `${clientId}`, 'roles'];
    yield put(setScreenData(isRolesReadyPath, false));
    const result = yield call(GET, `/iambff/v1/connected-apps/${clientId}/app-roles`, getHeaders());
    if (result.isSuccess) {
        const roles = get(result.response, 'roles', []);
        const sortedRoles = sortApplicationRoles(roles);
        yield put(setScreenData(rolesPath, sortedRoles, screenKey));
        yield put(setScreenData(isRolesReadyPath, true));
    } else {
        if (!isEmpty(result.errors)) {
            setErrorScreenMessage(result.errors[0].screenMessage, false, result.errors[0].errorMessages, screenKey);
        }
        yield put(setScreenData(isRolesReadyPath, true));
    }
}
